import { beHttp } from './beHttp';
import { PSI_PLATFORM_BE_URL } from './config';
import { getValidToken, isImpersonated } from './auth';

export type ComunidadNewsFeed = 'psi_platform' | 'institutional';

/** A quién se le muestra una novedad: todas, solo ciertas categorías, o todas menos esas. */
export type ComunidadAudienceMode = 'all' | 'include' | 'exclude';

export interface ComunidadNewsItem {
  id: string;
  feed: ComunidadNewsFeed;
  title: string;
  /** Texto breve que desarrolla la novedad, debajo del título — solo visible en el archivo
   *  completo ("Ver todas"), nunca en el adelanto chico de la sidebar. */
  body: string;
  /** URL de imagen, opcional — idem `body`. */
  imageUrl: string | null;
  /** URL de video (YouTube/Loom), opcional — idem `body`. */
  videoUrl: string | null;
  /** Botón de link opcional al pie de la novedad desplegada, opcional. */
  linkLabel: string | null;
  linkUrl: string | null;
  audienceMode: ComunidadAudienceMode;
  /** ranking_category (N, NZ, O, F) — vacío si `audienceMode` es 'all'. */
  audienceCategories: string[];
  order: number;
}

export interface ComunidadNewsletterEdition {
  id: string;
  editionNumber: number;
  month: string;
  /** Tema breve que se ve en el listado plegado del archivo. */
  theme: string;
  /** Cuerpo completo — solo se muestra al desplegar esa edición. */
  body: string;
  /** Pregunta de "Voces de la Red" ('' = esta edición no tiene). */
  voicesQuestion: string;
}

/** "Voces de la Red" desde el lado de la psicóloga: la pregunta, si todavía se puede
 *  responder (solo la última edición publicada) y lo que ya respondió. */
export interface MiVoz {
  editionNumber: number;
  question: string;
  open: boolean;
  mine: { answer: string; anonymous: boolean } | null;
}

export interface VocesEditionSummary {
  editionNumber: number;
  month: string;
  theme: string;
  question: string;
  open: boolean;
  count: number;
}

export interface VozAdminItem {
  name: string;
  surname: string;
  email: string;
  answer: string;
  /** Pidió que no aparezca su nombre si se comparte en la edición siguiente. */
  anonymous: boolean;
  updatedAt: string;
}

export interface VocesAdmin extends VocesEditionSummary {
  items: VozAdminItem[];
}

export type ComunidadCalendarEventType = 'formacion' | 'cafecito';

/** Evento del calendario de Comunidad (se carga mes a mes desde el panel de admin). */
export interface ComunidadCalendarEvent {
  id: string;
  type: ComunidadCalendarEventType;
  title: string;
  description: string;
  /** ISO (UTC). null = "Fecha a confirmar" (solo cafecitos). */
  startsAt: string | null;
  durationMinutes: number;
  meetUrl: string | null;
  /** Solo formaciones: agrupa sus ediciones (misma key que inscripciones/asistencia). */
  formacionKey: string | null;
  edition: number | null;
}

export interface ComunidadCalendarEventInput {
  type: ComunidadCalendarEventType;
  title: string;
  description: string;
  startsAt: string | null;
  durationMinutes: number;
  meetUrl: string | null;
  formacionKey?: string | null;
  edition?: number | null;
}

/** La formación del mes: la que maneja "Calificá" y la sección de participación. */
export interface ComunidadCurrentFormacion {
  key: string;
  title: string;
  /** ISO: se puede calificar desde que termina la 1ª edición… */
  calificaFrom: string;
  /** …hasta fin de ese mes (hora de Argentina). */
  calificaUntil: string;
}

export interface ComunidadFormacionOption {
  key: string;
  title: string;
  firstStartsAt: string | null;
}

export interface ComunidadPublishedContent {
  /** Adelanto (unas pocas), no el archivo completo — ver `getNewsPage` para eso. */
  psiPlatformNews: ComunidadNewsItem[];
  institutionalNews: ComunidadNewsItem[];
  /** De más nueva a más vieja — la [0] es la última edición. */
  newsletterEditions: ComunidadNewsletterEdition[];
  /** Lo que todavía no terminó + lo que no tiene fecha, ordenado por fecha. */
  calendarEvents: ComunidadCalendarEvent[];
  currentFormacion: ComunidadCurrentFormacion | null;
}

/** "Tu trayectoria en Psi Mammoliti" — números reales de la psicóloga autenticada. */
export interface ComunidadTrayectoria {
  sessionsThisYear: number;
  sessionsTotal: number;
  processesAccompanied: number;
  /** ISO — alta de la psicóloga en la plataforma. */
  joinedAt: string;
}

export interface FormacionInscripto {
  edition: number;
  name: string;
  surname: string;
  email: string;
  createdAt: string;
  /** null = todavía no se subió la asistencia de esa edición. */
  minutesAttended: number | null;
  attended: boolean | null;
}

/** "Avisar antes de esta edición": grupos a los que se avisaría (los arma el backend). */
export type RecordatorioGrupo = "anotadas" | "fueron_un_rato" | "no_fueron" | "sin_anotarse";

export interface RecordatorioPersona {
  psychologistId: string;
  name: string;
  surname: string;
  email: string;
  /** Ya recibió el recordatorio de esta edición: no le vuelve a llegar. */
  yaAvisada: boolean;
  nota: string | null;
}

export interface RecordatorioPreview {
  edition: number;
  startsAt: string;
  grupos: {
    grupo: RecordatorioGrupo;
    descripcion: string;
    title: string;
    message: string;
    personas: RecordatorioPersona[];
  }[];
}

export interface FormacionAdminSummary {
  key: string;
  title: string;
  editions: {
    edition: number;
    /** La confirmada al subir el informe de Meet; si todavía no, la del calendario. */
    durationMinutes: number;
    durationConfirmed: boolean;
    inscriptos: number;
    /** null = todavía no se subió el informe de Meet de esa edición. */
    participantes: number | null;
    asistentesSinInscripcion: string[];
  }[];
  inscriptos: FormacionInscripto[];
  calificaciones: {
    count: number;
    /** Promedio de 1 a 5, con un decimal. null si nadie calificó todavía. */
    average: number | null;
    /** `edition` = a cuál asistió quien calificó (para separar el feedback de la 1ª y la 2ª). */
    items: { edition: number; name: string; surname: string; email: string; rating: number; comment: string; createdAt: string }[];
  };
}

export interface FormacionParticipacion {
  participated: boolean;
  /** La calificación que ya dejó, si dejó. */
  calificacion: { rating: number; comment: string } | null;
  /** Datos del certificado — solo si participó (≥75%). */
  certificado: FormacionCertificado | null;
}

export interface FormacionCertificado {
  formacionKey: string;
  name: string;
  surname: string;
  formacionTitle: string;
  edition: number;
  /** ISO — fecha de la edición a la que asistió. */
  startsAt: string | null;
  durationMinutes: number;
}

export interface ComunidadPage<T> {
  data: T[];
  /** true si hay más para pedir con la página siguiente — mostrar "Cargar más". */
  hasMore: boolean;
}

/**
 * Chequeo de acceso al panel de admin de Comunidad — deliberadamente NO usa `beHttp`.
 * `beHttp` trata cualquier 401/403 como "sesión vencida" y redirige a `/login` (ver
 * `parseJson` en beHttp.ts); acá un 403 es la respuesta ESPERADA para el 99% de las
 * sesiones (cualquier psicóloga real, o CX impersonando a una real para soporte normal)
 * y no debe deslogear a nadie ni mostrar ningún error — simplemente significa "no
 * corresponde mostrar el botón del panel". Por eso esto hace un fetch crudo y nunca
 * lanza: cualquier respuesta que no sea 200 (o cualquier error de red) se interpreta
 * como "no tiene acceso", punto.
 */
export async function checkComunidadAdminAccess(): Promise<boolean> {
  try {
    // Atajo barato: si esta sesión no viene de una impersonación, ni vale la pena pegarle
    // al backend — nunca puede ser admin (ver ComunidadInternalGuard, la condición real).
    if (!isImpersonated()) return false;
    const token = getValidToken();
    if (!token) return false;
    const resp = await fetch(`${PSI_PLATFORM_BE_URL}/api/comunidad/admin/status`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    return resp.ok;
  } catch {
    return false;
  }
}

/**
 * Sube una imagen del editor visual (newsletter / novedades) — `fetch` crudo porque `beHttp`
 * manda JSON y esto es multipart. Devuelve la ruta con la que se referencia en el contenido.
 */
export async function uploadComunidadMedia(file: Blob, filename: string): Promise<{ id: string; url: string }> {
  const token = getValidToken();
  if (!token) throw new Error('Tu sesión venció. Volvé a ingresar.');
  const form = new FormData();
  form.append('file', file, filename);
  const resp = await fetch(`${PSI_PLATFORM_BE_URL}/api/comunidad/admin/media`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!resp.ok) {
    let message = 'No se pudo subir la imagen. Probá de nuevo.';
    try {
      const data = await resp.json();
      if (typeof data?.message === 'string') message = data.message;
    } catch {
      /* sin cuerpo: queda el mensaje genérico */
    }
    throw new Error(message);
  }
  return resp.json();
}

export const comunidadAdapter = {
  /** Contenido YA PUBLICADO — es lo que consume la pantalla real de Comunidad (un adelanto,
   *  no el archivo completo). */
  async getContent(): Promise<ComunidadPublishedContent> {
    return beHttp.get<ComunidadPublishedContent>('/api/comunidad/content');
  },

  /** Panel de admin: eventos del calendario en borrador. */
  async listDraftCalendar(): Promise<ComunidadCalendarEvent[]> {
    const { data } = await beHttp.get<{ data: ComunidadCalendarEvent[] }>('/api/comunidad/admin/calendar');
    return data;
  },

  async createCalendarEvent(input: ComunidadCalendarEventInput): Promise<ComunidadCalendarEvent> {
    return beHttp.post<ComunidadCalendarEvent>('/api/comunidad/admin/calendar', input);
  },

  async updateCalendarEvent(id: string, input: ComunidadCalendarEventInput): Promise<ComunidadCalendarEvent> {
    return beHttp.put<ComunidadCalendarEvent>(`/api/comunidad/admin/calendar/${id}`, input);
  },

  async deleteCalendarEvent(id: string): Promise<void> {
    await beHttp.delete(`/api/comunidad/admin/calendar/${id}`);
  },

  /** Panel de admin: formaciones conocidas (para sumar ediciones y ver inscriptos). */
  async listFormacionOptions(): Promise<ComunidadFormacionOption[]> {
    const { data } = await beHttp.get<{ data: ComunidadFormacionOption[] }>('/api/comunidad/admin/formaciones');
    return data;
  },

  /** Registra la inscripción al tocar "Anotarme" en una edición (idempotente). */
  async inscribirFormacion(key: string, edition: number): Promise<void> {
    await beHttp.post(`/api/comunidad/formaciones/${key}/inscripcion`, { edition });
  },

  /** ¿La psicóloga autenticada participó (≥75%) de alguna edición? + su calificación, si ya dejó. */
  /** Panel: grupos a los que se avisaría antes de una edición, con nombres y textos (no envía nada). */
  async getRecordatorioPreview(key: string, edition: number): Promise<RecordatorioPreview> {
    return beHttp.get<RecordatorioPreview>(`/api/comunidad/admin/formaciones/${key}/recordatorio/${edition}`);
  },

  /** Panel: manda el recordatorio a las elegidas. Devuelve a cuántas se les manda. */
  async sendRecordatorio(
    key: string,
    edition: number,
    psychologistIds: string[],
    /** Solo los grupos cuyo texto se editó en el panel. */
    textos: { grupo: RecordatorioGrupo; title: string; message: string }[] = [],
  ): Promise<number> {
    const res = await beHttp.post<{ avisados?: number }>(`/api/comunidad/admin/formaciones/${key}/recordatorio`, {
      edition,
      psychologistIds,
      textos,
    });
    return res?.avisados ?? 0;
  },

  /** Panel: cuántas recibirían ahora el aviso "Ya está tu certificado" (no envía nada). */
  async countAvisosCertificado(key: string): Promise<number> {
    const res = await beHttp.get<{ pendientes?: number }>(`/api/comunidad/admin/formaciones/${key}/avisos-certificado`);
    return res?.pendientes ?? 0;
  },

  /** Panel: manda el aviso a quienes llegaron al 75% y no lo recibieron (no toca la asistencia). */
  async sendAvisosCertificado(key: string): Promise<number> {
    const res = await beHttp.post<{ avisados?: number }>(`/api/comunidad/admin/formaciones/${key}/avisos-certificado`, {});
    return res?.avisados ?? 0;
  },

  /** "Tus certificados": todas las formaciones en las que participó (≥75%), la más nueva primero. */
  async listCertificados(): Promise<FormacionCertificado[]> {
    const res = await beHttp.get<{ data?: FormacionCertificado[] }>('/api/comunidad/certificados');
    return Array.isArray(res?.data) ? res.data : [];
  },

  async getFormacionParticipacion(key: string): Promise<FormacionParticipacion> {
    return beHttp.get<FormacionParticipacion>(`/api/comunidad/formaciones/${key}/participacion`);
  },

  /** Calificación 1–5 + comentario opcional (solo para quien participó). */
  async calificarFormacion(key: string, rating: number, comment: string): Promise<void> {
    await beHttp.post(`/api/comunidad/formaciones/${key}/calificacion`, {
      rating,
      ...(comment ? { comment } : {}),
    });
  },

  /** Panel de admin: inscriptos por edición + asistencia cruzada. */
  async getFormacionAdmin(key: string): Promise<FormacionAdminSummary> {
    return beHttp.get<FormacionAdminSummary>(`/api/comunidad/admin/formaciones/${key}`);
  },

  /** Panel de admin: carga (o reemplaza) la asistencia de una edición desde el informe de Meet. */
  async uploadFormacionAsistencia(
    key: string,
    edition: number,
    rows: { email: string; minutes: number }[],
    durationMinutes: number,
  ): Promise<{ participantes: number; total: number; avisados: number }> {
    return beHttp.put<{ participantes: number; total: number; avisados: number }>(
      `/api/comunidad/admin/formaciones/${key}/asistencia`,
      { edition, rows, durationMinutes },
    );
  },

  /** Trayectoria de la psicóloga autenticada (el id sale del token en el backend). */
  async getTrayectoria(): Promise<ComunidadTrayectoria> {
    return beHttp.get<ComunidadTrayectoria>('/api/comunidad/trayectoria');
  },

  /** Archivo completo de una columna de novedades, paginado — usado por "Ver todas". */
  async getNewsPage(
    feed: ComunidadNewsFeed,
    page: number,
    pageSize = 10,
  ): Promise<ComunidadPage<ComunidadNewsItem>> {
    return beHttp.get<ComunidadPage<ComunidadNewsItem>>(`/api/comunidad/news/${feed}`, {
      page,
      pageSize,
    });
  },

  /** Archivo completo del newsletter, paginado — usado por "Leer entero" → archivo. */
  async getNewsletterPage(
    page: number,
    pageSize = 5,
  ): Promise<ComunidadPage<ComunidadNewsletterEdition>> {
    return beHttp.get<ComunidadPage<ComunidadNewsletterEdition>>(
      '/api/comunidad/newsletter/editions',
      { page, pageSize },
    );
  },

  // ---- Panel de admin (todo esto opera sobre el BORRADOR) ----

  async listDraftNews(feed: ComunidadNewsFeed): Promise<ComunidadNewsItem[]> {
    const { data } = await beHttp.get<{ data: ComunidadNewsItem[] }>(
      '/api/comunidad/admin/news',
      { feed },
    );
    return data;
  },

  async createNewsItem(
    feed: ComunidadNewsFeed,
    title: string,
    body: string,
    imageUrl?: string,
    videoUrl?: string,
    linkLabel?: string,
    linkUrl?: string,
    audience?: { audienceMode: ComunidadAudienceMode; audienceCategories: string[] },
  ): Promise<ComunidadNewsItem> {
    return beHttp.post<ComunidadNewsItem>('/api/comunidad/admin/news', {
      feed,
      title,
      body,
      imageUrl,
      videoUrl,
      linkLabel,
      linkUrl,
      ...audience,
    });
  },

  async updateNewsItem(
    id: string,
    title: string,
    body: string,
    imageUrl?: string,
    videoUrl?: string,
    linkLabel?: string,
    linkUrl?: string,
    audience?: { audienceMode: ComunidadAudienceMode; audienceCategories: string[] },
  ): Promise<ComunidadNewsItem> {
    return beHttp.put<ComunidadNewsItem>(`/api/comunidad/admin/news/${id}`, {
      title,
      body,
      imageUrl,
      videoUrl,
      linkLabel,
      linkUrl,
      ...audience,
    });
  },

  async deleteNewsItem(id: string): Promise<void> {
    await beHttp.delete(`/api/comunidad/admin/news/${id}`);
  },

  async reorderNews(feed: ComunidadNewsFeed, orderedIds: string[]): Promise<void> {
    await beHttp.put('/api/comunidad/admin/news/reorder', { feed, orderedIds });
  },

  async listDraftNewsletterEditions(): Promise<ComunidadNewsletterEdition[]> {
    const { data } = await beHttp.get<{ data: ComunidadNewsletterEdition[] }>(
      '/api/comunidad/admin/newsletter',
    );
    return data;
  },

  async createNewsletterEdition(
    month: string,
    theme: string,
    body: string,
    voicesQuestion: string,
  ): Promise<ComunidadNewsletterEdition> {
    return beHttp.post<ComunidadNewsletterEdition>('/api/comunidad/admin/newsletter', { month, theme, body, voicesQuestion });
  },

  async updateNewsletterEdition(
    id: string,
    month: string,
    theme: string,
    body: string,
    voicesQuestion: string,
  ): Promise<ComunidadNewsletterEdition> {
    return beHttp.put<ComunidadNewsletterEdition>(`/api/comunidad/admin/newsletter/${id}`, { month, theme, body, voicesQuestion });
  },

  /** "Voces de la Red": la pregunta de una edición y lo que ya respondió la psicóloga. */
  async getMiVoz(editionNumber: number): Promise<MiVoz> {
    return beHttp.get<MiVoz>(`/api/comunidad/newsletter/${editionNumber}/voz`);
  },

  /** Una respuesta por edición: si vuelve a enviar, se reemplaza la anterior. */
  async responderVoz(editionNumber: number, answer: string, anonymous: boolean): Promise<MiVoz> {
    return beHttp.put<MiVoz>(`/api/comunidad/newsletter/${editionNumber}/voz`, { answer, anonymous });
  },

  /** Panel de admin: ediciones con pregunta y cuántas respuestas tiene cada una. */
  async listVocesEditions(): Promise<VocesEditionSummary[]> {
    const { data } = await beHttp.get<{ data: VocesEditionSummary[] }>('/api/comunidad/admin/voces');
    return data;
  },

  async getVocesAdmin(editionNumber: number): Promise<VocesAdmin> {
    return beHttp.get<VocesAdmin>(`/api/comunidad/admin/voces/${editionNumber}`);
  },

  async deleteNewsletterEdition(id: string): Promise<void> {
    await beHttp.delete(`/api/comunidad/admin/newsletter/${id}`);
  },

  async publish(): Promise<ComunidadPublishedContent> {
    return beHttp.post<ComunidadPublishedContent>('/api/comunidad/admin/publish', {}, {
      showSuccessToast: true,
      successMessage: 'Comunidad actualizada — los cambios ya están publicados',
    });
  },
};

export default comunidadAdapter;
